import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Collapse, Divider, IconButton, InputLabel, LinearProgress, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { ExpandMore, ExpandLess, OpenInNew, Search, StoreMallDirectory } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { storeBudgetTrackerReport } from "data/storeBudgetData";
import { arrType, getColDefaultValue } from "data/globalData";
import RenderInputField from "components/EboStoreV4/CommonComponents/RenderInputFiled/RenderInputFiled";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { D3BudgetVsActual, D3DonutChart, D3GroupedBarChart, D3GroupedHorizontalBarChart, D3HorizontalBarChart, D3MultiLineChart, D3SimpleBarChart, D3StackedHorizontalBarChart, formatPct } from "./StoreBudgetD3Charts";

const formatCurrency = (v) => `Rs. ${Number(v || 0).toLocaleString("en-IN")}`;
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB").replace(/\//g, "-");
};
const groupTooltipItems = (items = [], extraLine) => {
  const poMap = new Map();
  items.forEach((item) => {
    const key = item.po_number || item.barcode || "PO";
    if (!poMap.has(key)) {
      poMap.set(key, {
        po_number: key,
        vendor: item.vendor,
        material: item.material,
        invoice_no: item.invoice_no,
        grn_no: item.grn_no,
        po_date: item.po_date,
        grn_date: item.grn_date,
        amount: 0,
        count: 0,
      });
    }
    const entry = poMap.get(key);
    entry.amount += Number(item.amount) || 0;
    entry.count += 1;
    if (!entry.vendor && item.vendor) entry.vendor = item.vendor;
    if (!entry.material && item.material) entry.material = item.material;
    if (!entry.invoice_no && item.invoice_no) entry.invoice_no = item.invoice_no;
    if (!entry.grn_no && item.grn_no) entry.grn_no = item.grn_no;
    if (!entry.po_date && item.po_date) entry.po_date = item.po_date;
    if (!entry.grn_date && item.grn_date) entry.grn_date = item.grn_date;
  });
  const grouped = Array.from(poMap.values()).sort((a, b) => b.amount - a.amount);
  const details = grouped.map((item) => ({
    title: item.po_number,
    lines: [
      [item.vendor, item.material].filter(Boolean).join(" · "),
      item.po_date ? `PO date: ${formatDate(item.po_date)}` : null,
      extraLine ? extraLine(item) : null,
      `${formatCurrency(item.amount)} · ${item.count} item${item.count === 1 ? "" : "s"}`,
    ].filter(Boolean),
  }));
  return { details, poCount: poMap.size, itemCount: items.length };
};

const vendorDisplayName = (name) => String(name || "").trim() || "Unknown vendor";
const normalizePartyName = (name) => vendorDisplayName(name).toLowerCase().replace(/\s+/g, " ");
const getSoDetails = (budget) => budget?.so_details || budget?.service_orders || [];
const getApDetails = (budget) => budget?.ap_voucher_details || budget?.ap_vouchers || [];

const classifyVendorStatus = (row) => {
  const status = String(row?.item_status || "").toLowerCase();
  if (status.includes("cancel")) return "Cancelled";
  if (status.includes("complet")) return "GRN Received";
  if (status === "pending" || status === "new") return "Pending";
  return row?.item_status || "Pending";
};

const downloadVendorExcel = async (vendorName, poDetails = []) => {
  const vendorKey = normalizePartyName(vendorName);
  const rows = (poDetails || []).filter((row) => normalizePartyName(row.vendor_name) === vendorKey);
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Vendor data");
  sheet.columns = [
    { header: "Vendor Name", key: "vendor_name", width: 28 },
    { header: "PO Number", key: "po_number", width: 22 },
    { header: "PO Date", key: "po_date", width: 14 },
    { header: "Barcode", key: "barcode", width: 14 },
    { header: "Material", key: "material", width: 16 },
    { header: "Section", key: "section", width: 16 },
    { header: "Status", key: "status", width: 16 },
    { header: "Item Status", key: "item_status", width: 16 },
    { header: "PO Item Value", key: "po_item_value", width: 16 },
    { header: "PO Item With Tax", key: "po_item_with_tax", width: 18 },
    { header: "Invoice No", key: "invoice_no", width: 18 },
    { header: "Invoice Date", key: "invoice_date", width: 14 },
    { header: "Invoice Value (w/o tax)", key: "invoice_value_without_tax", width: 22 },
    { header: "Invoice Value (w tax)", key: "invoice_value_with_tax", width: 20 },
    { header: "GRN No", key: "grn_no", width: 20 },
    { header: "GRN Date", key: "grn_date", width: 14 },
    { header: "Store Name", key: "store_name", width: 24 },
    { header: "Site Code", key: "site_code", width: 14 },
    { header: "PO Remark", key: "po_remark", width: 22 },
  ];
  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDBEAFE" } };
  rows.forEach((row) => {
    sheet.addRow({
      vendor_name: vendorDisplayName(row.vendor_name),
      po_number: row.po_number || "",
      po_date: formatDate(row.po_date),
      barcode: row.barcode || "",
      material: row.material || "",
      section: row.section || "",
      status: classifyVendorStatus(row),
      item_status: row.item_status || "",
      po_item_value: Number(row.po_item_value) || 0,
      po_item_with_tax: Number(row.po_item_with_tax) || 0,
      invoice_no: row.invoice_no || "",
      invoice_date: formatDate(row.invoice_date),
      invoice_value_without_tax: Number(row.invoice_value_without_tax) || 0,
      invoice_value_with_tax: Number(row.invoice_value_with_tax) || 0,
      grn_no: row.grn_no || "",
      grn_date: formatDate(row.grn_date),
      store_name: row.store_name || "",
      site_code: row.site_code || "",
      po_remark: row.po_remark || "",
    });
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const safeName = vendorName.replace(/[<>:"/\\|?*]+/g, "_").slice(0, 80) || "vendor";
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `${safeName}_vendor_data.xlsx`
  );
};

const downloadVendorApExcel = async (vendorName, apDetails = []) => {
  const vendorKey = normalizePartyName(vendorName);
  const rows = (apDetails || []).filter((row) => normalizePartyName(row.name) === vendorKey);
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("AP Vouchers");
  sheet.columns = [
    { header: "Name", key: "name", width: 28 },
    { header: "Voucher Date", key: "voucher_date", width: 14 },
    { header: "Voucher No", key: "voucher_number", width: 18 },
    { header: "Cash / Bank Ledger", key: "cash_bank_ledger", width: 24 },
    { header: "Narration", key: "narration", width: 32 },
    { header: "Instrument No", key: "instrument_no", width: 18 },
    { header: "Amount", key: "amount", width: 16 },
    { header: "Ref Site", key: "ref_site", width: 18 },
  ];
  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFED7AA" } };
  rows.forEach((row) => {
    sheet.addRow({
      name: vendorDisplayName(row.name),
      voucher_date: formatDate(row.voucher_date),
      voucher_number: row.voucher_number || "",
      cash_bank_ledger: row.cash_bank_ledger || "",
      narration: row.narration || "",
      instrument_no: row.instrument_no || "",
      amount: Number(row.amount) || 0,
      ref_site: row.ref_site || "",
    });
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const safeName = String(vendorName || "vendor").replace(/[<>:"/\\|?*]+/g, "_").slice(0, 80) || "vendor";
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `${safeName}_ap_voucher.xlsx`
  );
};

const getShortCodeLabel = (label) => {
  const parts = String(label || "").split("/");
  // if (parts.length <= 2) return parts.join("/");
  // return parts.slice(-2).join("/");
  return parts[1]
};

const cardShell = {
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fff",
};

const StatCard = ({ label, value, sub, sub1, color = "#1e40af", bg = "#f8fbff" }) => (
  <Box sx={{ flex: 1, minWidth: 160, p: 2, ...cardShell, backgroundColor: bg }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.3}>
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700} sx={{ color }} lineHeight={1.3} mt={0.25}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary" display="block">
        {sub}
      </Typography>
    )}
    {sub1 && (
      <Typography variant="caption" color="text.secondary" display="block">
        {sub1}
      </Typography>
    )}
  </Box>
);

const ChartCard = ({ title, subtitle, children, sx = {} }) => (
  <Box sx={{ p: 0, overflow: "hidden", ...cardShell, ...sx }}>
    <Box sx={{ px: 2, py: 1.25, backgroundColor: "#dbeafe", borderBottom: "1px solid #bfdbfe" }}>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const computePoSummary = (poDetails = [], soDetails = [], apDetails = []) => {
  const poMap = new Map();
  const grnMap = new Map();
  let totalGrnWithTax = 0;
  let totalGrnWithoutTax = 0;

  poDetails.forEach((row) => {
    const poNo = row.po_number;
    const grnNo = row.grn_no || row.invoice_no || row.id || poNo;
    const itemValue = Number(row.po_item_value) || 0;
    const itemWithTax = Number(row.po_item_with_tax) || 0;
    const invWithTax = Number(row.invoice_value_with_tax) || 0;
    const invWithoutTax = Number(row.invoice_value_without_tax) || 0;
    const status = String(row.item_status || "").toUpperCase();

    if (!poMap.has(poNo)) {
      poMap.set(poNo, {
        po_number: poNo,
        po_value: 0,
        po_tax: 0,
        po_with_tax: 0,
        material: row.material,
        canceled: 0,
        completed: 0,
        pending: 0,
      });
    }

    const poEntry = poMap.get(poNo);
    // Use line-item amounts so mixed cancel + GRN POs are not treated as fully cancelled
    poEntry.po_value += itemValue;
    poEntry.po_with_tax += itemWithTax;
    poEntry.po_tax = poEntry.po_with_tax - poEntry.po_value;
    if (!poEntry.material && row.material) poEntry.material = row.material;

    if (status.includes("CANCEL")) {
      poEntry.canceled += itemWithTax;
    } else if (status.includes("COMPLET")) {
      const grnKey = `${poNo}__${grnNo}`;
      if (!grnMap.has(grnKey)) {
        grnMap.set(grnKey, {
          po_number: poNo,
          grn_no: grnNo,
          value_with_tax: 0,
          value_without_tax: 0,
          material: row.material,
        });
      }

      const grnEntry = grnMap.get(grnKey);
      grnEntry.value_with_tax += invWithTax;
      grnEntry.value_without_tax += invWithoutTax;
      if (!grnEntry.material && row.material) grnEntry.material = row.material;
    } else {
      // pending / new / other open statuses
      poEntry.pending += itemWithTax;
    }

    totalGrnWithTax += invWithTax;
    totalGrnWithoutTax += invWithoutTax;
  });

  const grnBreakdown = Array.from(grnMap.values());
  const grnTotalsByPo = grnBreakdown.reduce((acc, item) => {
    acc.set(item.po_number, (acc.get(item.po_number) || 0) + item.value_with_tax);
    return acc;
  }, new Map());

  const distinctPOs = Array.from(poMap.values());
  distinctPOs.forEach((po) => {
    po.completed = grnTotalsByPo.get(po.po_number) || 0;
  });
  // const totalPoBase = distinctPOs.reduce((s, p) => s + p.po_value, 0);
  // const totalPoTax = distinctPOs.reduce((s, p) => s + p.po_tax, 0);
  const totalPoBase = poDetails.reduce((s, p) => s + (Number(p.po_item_value) || 0), 0);
  const totalPoWTax = poDetails.reduce((s, p) => s + (Number(p.po_item_with_tax) || 0), 0);
  const totalPoTax = totalPoWTax - totalPoBase;
  const totalPoWithTax = totalPoWTax

  const canceledItemAmount = poDetails.reduce((sum, row) => {
    const normalizedGrnStatus = String(row?.item_status || "").toLowerCase();
    if (!normalizedGrnStatus.includes("cancel")) return sum;
    return sum + (Number(row?.po_item_with_tax) || 0);
  }, 0);
  const canceledItemBAmount = poDetails.reduce((sum, row) => {
    const normalizedGrnStatus = String(row?.item_status || "").toLowerCase();
    if (!normalizedGrnStatus.includes("cancel")) return sum;
    return sum + (Number(row?.po_item_value) || 0);
  }, 0);
  const pendingItemAmount = poDetails.reduce((sum, row) => {
    const normalizedGrnStatus = String(row?.item_status || "").toLowerCase();
    if (normalizedGrnStatus !== "pending" &&
    normalizedGrnStatus !== "new") return sum;
    return sum + (Number(row?.po_item_with_tax) || 0);
  }, 0);
  const pendingItemValue = poDetails.reduce((sum, row) => {
    const normalizedGrnStatus = String(row?.item_status || "").toLowerCase();
    if (normalizedGrnStatus !== "pending" &&
    normalizedGrnStatus !== "new") return sum;
    return sum + (Number(row?.po_item_value) || 0);
  }, 0);
  const totalCanceled = canceledItemAmount;
  const totalPending = pendingItemAmount;
  const overallPoAmount = totalPoBase - canceledItemBAmount;

  // section breakdown for the pie chart; fall back to material for older rows
  const sectionMap = new Map();
  poDetails.forEach((row) => {
    const section = row.section || row.material || "Other";
    const val = Number(row.invoice_value_with_tax) || 0;
    sectionMap.set(section, (sectionMap.get(section) || 0) + val);
  });

  const pendingByMaterialMap = new Map();
  const agingBuckets = [
    { label: "0–15 days", key: "d0_15", min: 0, max: 15, color: "#10b981", value: 0, count: 0, items: [] },
    { label: "16–30 days", key: "d16_30", min: 16, max: 30, color: "#f59e0b", value: 0, count: 0, items: [] },
    { label: "31–60 days", key: "d31_60", min: 31, max: 60, color: "#f97316", value: 0, count: 0, items: [] },
    { label: "60+ days", key: "d60_plus", min: 61, max: Infinity, color: "#ef4444", value: 0, count: 0, items: [] },
  ];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  poDetails.forEach((row) => {
    const status = String(row?.item_status || "").toLowerCase();
    if (status !== "pending" && status !== "new") return;
    const amount = Number(row.po_item_with_tax) || 0;
    if (!amount) return;
    const poDate = row.po_date ? new Date(row.po_date) : null;
    if (!poDate || Number.isNaN(poDate.getTime())) return;
    const days = Math.max(0, Math.floor((today - poDate) / 86400000));
    const bucket = agingBuckets.find((item) => days >= item.min && days <= item.max);
    if (!bucket) return;

    const material = row.material || "Other";
    if (!pendingByMaterialMap.has(material)) {
      pendingByMaterialMap.set(material, {
        material,
        value: 0,
        count: 0,
        d0_15: 0,
        d16_30: 0,
        d31_60: 0,
        d60_plus: 0,
        itemsByKey: { d0_15: [], d16_30: [], d31_60: [], d60_plus: [] },
      });
    }
    const materialEntry = pendingByMaterialMap.get(material);
    materialEntry.value += amount;
    materialEntry.count += 1;
    materialEntry[bucket.key] += amount;

    const itemDetail = {
      po_number: row.po_number || "PO",
      vendor: row.vendor_name || "Unknown vendor",
      material: row.material || "",
      barcode: row.barcode || "",
      po_date: row.po_date || "",
      amount,
      days,
    };
    materialEntry.itemsByKey[bucket.key].push(itemDetail);

    bucket.value += amount;
    bucket.count += 1;
    bucket.items.push(itemDetail);
  });

  const pendingByMaterial = Array.from(pendingByMaterialMap.values())
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const pendingAging = agingBuckets.some((item) => item.count > 0) ? agingBuckets : [];

  const vendorMap = new Map();
  const ensureVendor = (rawName) => {
    const display = vendorDisplayName(rawName);
    const key = normalizePartyName(display);
    if (!vendorMap.has(key)) {
      vendorMap.set(key, { name: display, pending: 0, grn: 0, cancelled: 0, ap: 0 });
    }
    return vendorMap.get(key);
  };
  poDetails.forEach((row) => {
    const entry = ensureVendor(row.vendor_name);
    const status = String(row?.item_status || "").toLowerCase();
    if (status.includes("cancel")) {
      entry.cancelled += Number(row.po_item_with_tax) || 0;
    } else if (status.includes("complet")) {
      entry.grn += Number(row.invoice_value_with_tax) || Number(row.po_item_with_tax) || 0;
    } else {
      entry.pending += Number(row.po_item_with_tax) || 0;
    }
  });
  apDetails.forEach((row) => {
    const entry = ensureVendor(row.name);
    entry.ap += Number(row.amount) || 0;
  });
  const vendorBreakdown = Array.from(vendorMap.values())
    .map((row) => {
      const po = (Number(row.pending) || 0) + (Number(row.grn) || 0);
      const ap = Number(row.ap) || 0;
      return { ...row, po, ap, value: po + ap };
    })
    .filter((row) => (Number(row.po) || 0) > 0 || (Number(row.ap) || 0) > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  const materialPoMap = new Map();
  poDetails.forEach((row) => {
    const material = row.material || "Other";
    materialPoMap.set(material, (materialPoMap.get(material) || 0) + (Number(row.po_item_with_tax) || 0));
  });
  const materialBreakdown = Array.from(materialPoMap.entries())
    .map(([name, value]) => ({ name, value }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);

  const monthMap = new Map();
  const toMonthKey = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  };
  const ensureMonth = (key) => {
    if (!monthMap.has(key)) monthMap.set(key, { po: 0, grn: 0, so: 0, ap: 0, poItems: [], grnItems: [], soItems: [], apItems: [] });
    return monthMap.get(key);
  };
  poDetails.forEach((row) => {
    const poKey = row.po_date ? toMonthKey(row.po_date) : null;
    if (poKey) {
      const entry = ensureMonth(poKey);
      const amount = Number(row.po_item_with_tax) || 0;
      entry.po += amount;
      entry.poItems.push({
        po_number: row.po_number || "PO",
        vendor: row.vendor_name || "Unknown vendor",
        material: row.material || "",
        po_date: row.po_date || "",
        amount,
      });
    }
    const grnKey = row.grn_date ? toMonthKey(row.grn_date) : null;
    const grnAmount = Number(row.invoice_value_with_tax) || 0;
    if (grnKey && grnAmount > 0) {
      const entry = ensureMonth(grnKey);
      entry.grn += grnAmount;
      entry.grnItems.push({
        po_number: row.po_number || "PO",
        vendor: row.vendor_name || "Unknown vendor",
        material: row.material || "",
        invoice_no: row.invoice_no || "",
        grn_no: row.grn_no || "",
        po_date: row.po_date || "",
        grn_date: row.grn_date || "",
        amount: grnAmount,
      });
    }
  });
  soDetails.forEach((row) => {
    const soKey = row.order_date ? toMonthKey(row.order_date) : null;
    if (!soKey) return;
    const entry = ensureMonth(soKey);
    const amount = Number(row.service_amount) || 0;
    entry.so += amount;
    entry.soItems.push({
      po_number: row.order_no || "SO",
      vendor: row.supplier_name || "Unknown supplier",
      material: row.service_ledger || "",
      po_date: row.order_date || "",
      amount,
    });
  });
  apDetails.forEach((row) => {
    const apKey = row.voucher_date ? toMonthKey(row.voucher_date) : null;
    if (!apKey) return;
    const entry = ensureMonth(apKey);
    const amount = Number(row.amount) || 0;
    entry.ap += amount;
    entry.apItems.push({
      po_number: row.voucher_number || "AP",
      vendor: row.name || "Unknown",
      material: row.narration || row.cash_bank_ledger || "",
      invoice_no: row.instrument_no || "",
      po_date: row.voucher_date || "",
      amount,
    });
  });
  const monthlyTrend = Array.from(monthMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, vals]) => {
      const [year, month] = key.split("-");
      const label = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("en-GB", {
        month: "short",
        year: "2-digit",
      });
      return {
        key,
        label,
        po: vals.po,
        grn: vals.grn,
        so: vals.so || 0,
        ap: vals.ap || 0,
        poItems: vals.poItems,
        grnItems: vals.grnItems,
        soItems: vals.soItems || [],
        apItems: vals.apItems || [],
      };
    });

  const statusMap = new Map();
  poDetails.forEach((row) => {
    const normalizedGrnStatus = String(row?.item_status || "").toLowerCase();
    let label = "Other";
    let value = Number(row?.po_item_value) || 0;

    if (normalizedGrnStatus.includes("cancel")) {
      label = "Cancelled";
      value = Number(row?.po_item_value) || 0;
    } else if (normalizedGrnStatus.includes("complet")) {
      label = "GRN Received";
      value = Number(row?.invoice_value_without_tax) || Number(row?.po_item_value) || 0;
    } else if (normalizedGrnStatus === "pending" || normalizedGrnStatus === "new") {
      label = "Pending for GRN";
      value = Number(row?.po_item_value) || 0;
    }

    statusMap.set(label, (statusMap.get(label) || 0) + value);
  });

  const soAmount = soDetails.reduce((sum, row) => sum + (Number(row.service_amount) || 0), 0);
  const soCount = new Set(soDetails.map((row) => row.order_no).filter(Boolean)).size;
  const soLedgerMap = new Map();
  soDetails.forEach((row) => {
    const amount = Number(row.service_amount) || 0;
    if (!amount) return;
    const ledger = row.service_ledger || "Other";
    soLedgerMap.set(ledger, (soLedgerMap.get(ledger) || 0) + amount);
  });
  const soLedgerBreakdown = Array.from(soLedgerMap.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const apAmount = apDetails.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const apCount = new Set(apDetails.map((row) => row.voucher_number).filter(Boolean)).size;
  const apNameMap = new Map();
  apDetails.forEach((row) => {
    const amount = Number(row.amount) || 0;
    if (!amount) return;
    const name = row.name || "Unknown";
    apNameMap.set(name, (apNameMap.get(name) || 0) + amount);
  });
  const apNameBreakdown = Array.from(apNameMap.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  return {
    distinctPOs,
    grnBreakdown,
    pendingByMaterial,
    totalPoBase,
    totalPoTax,
    totalPoWithTax,
    totalCanceled,
    totalPending,
    canceledItemBAmount,
    pendingItemValue,
    overallPoAmount,
    totalGrnWithTax,
    totalGrnWithoutTax,
    sectionBreakdown: Array.from(sectionMap.entries()).map(([name, value]) => ({ name, value })),
    poStatusBreakdown: Array.from(statusMap.entries())
      .map(([name, value]) => ({ name, value }))
      .filter((row) => row.value > 0)
      .sort((a, b) => {
        const order = { "GRN Received": 0, "Pending for GRN": 1, Cancelled: 2, Other: 3 };
        return (order[a.name] ?? 99) - (order[b.name] ?? 99);
      }),
    poCount: distinctPOs.length,
    rowCount: poDetails.length,
    vendorBreakdown,
    materialBreakdown,
    pendingAging,
    monthlyTrend,
    soAmount,
    soCount,
    soLedgerBreakdown,
    apAmount,
    apCount,
    apNameBreakdown,
  };
};

const buildPendingPoTooltipDetails = (items = []) => {
  const poMap = new Map();
  items.forEach((item) => {
    const key = item.po_number || item.barcode || "PO";
    if (!poMap.has(key)) {
      poMap.set(key, {
        po_number: key,
        vendor: item.vendor,
        material: item.material,
        po_date: item.po_date,
        amount: 0,
        days: 0,
        count: 0,
      });
    }
    const entry = poMap.get(key);
    entry.amount += Number(item.amount) || 0;
    entry.count += 1;
    entry.days = Math.max(Number(entry.days) || 0, Number(item.days) || 0);
    if (!entry.vendor && item.vendor) entry.vendor = item.vendor;
    if (!entry.material && item.material) entry.material = item.material;
    if (!entry.po_date && item.po_date) entry.po_date = item.po_date;
  });
  return Array.from(poMap.values())
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 12)
    .map((item) => ({
      title: item.po_number,
      lines: [
        [item.vendor, item.material].filter(Boolean).join(" · "),
        item.po_date ? `PO date: ${formatDate(item.po_date)}` : null,
        `${formatCurrency(item.amount)} · ${item.days} day${item.days === 1 ? "" : "s"} pending · ${item.count} item${item.count === 1 ? "" : "s"}`,
      ].filter(Boolean),
    }));
};

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316"];

const PENDING_AGING_SERIES = [
  { key: "d0_15", label: "0–15 days", color: "#10b981" },
  { key: "d16_30", label: "16–30 days", color: "#f59e0b" },
  { key: "d31_60", label: "31–60 days", color: "#f97316" },
  { key: "d60_plus", label: "60+ days", color: "#ef4444" },
];

const VENDOR_CONCENTRATION_SERIES = [
  { key: "po", label: "PO present", color: "#6366f1" },
  { key: "ap", label: "AP voucher paid", color: "#c2410c" },
];

const BudgetCard = ({ budget }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const openStoreBudget = (event) => {
    event.stopPropagation();
    navigate(`/explore/store_budget_tracker/create_store_budget`, {
      state: { data: budget },
    });
  };

  const totalBudget = Number(budget?.total_budget) || 0;
  const isSubmitted = budget?.is_submitted === true || budget?.status === "submitted";
  const po = useMemo(
    () => computePoSummary(budget?.po_details || [], getSoDetails(budget), getApDetails(budget)),
    [budget]
  );

  const soAmount = Number(po.soAmount) || 0;
  const apAmount = Number(po.apAmount) || 0;
  const budgetUsed = Math.max((Number(po.overallPoAmount) || 0) + soAmount, 0);
  const remaining = Math.max(totalBudget - budgetUsed, 0);
  const exceeded = budgetUsed > totalBudget ? budgetUsed - totalBudget : 0;
  const utilization = totalBudget ? ((budgetUsed / totalBudget) * 100).toFixed(1) : "0.0";
  const isOver = Number(utilization) > 100;
  const budgetBase = Number(totalBudget) || 0;
  const grnBase = Number(po.totalGrnWithoutTax) || 0;
  const pendingBase = Number(po.pendingItemValue) || 0;
  const cancelledBase = Number(po.canceledItemBAmount) || 0;

  const poStatusPieData = (po.poStatusBreakdown || [])
    .map((row) => {
      const color =
        row.name === "GRN Received" ? "#10b981" :
        row.name === "Pending for GRN" ? "#f59e0b" :
        row.name === "Cancelled" ? "#ef4444" :
        "#94a3b8";
      return {
        id: row.name,
        value: row.value,
        label: row.name,
        color,
      };
    })
    .filter((d) => d.value > 0);

  const sectionPieData = po.sectionBreakdown.map((m, i) => ({
    id: i, value: m.value, label: m.name, color: COLORS[i % COLORS.length],
  }));
  const hasPoStatusData = poStatusPieData.length > 0;
  const hasSectionData = sectionPieData.some((item) => Number(item.value) > 0);
  const hasBudgetVsActualData = [budgetBase, grnBase, pendingBase, cancelledBase, soAmount].some((value) => Number(value) > 0);
  const hasPoLevelData = po.distinctPOs.some((item) => {
    const pendingValue = Number(item.pending) || 0;
    return [item.po_with_tax, item.canceled, item.completed, pendingValue].some((value) => Number(value) > 0);
  });
  const hasPendingByMaterialData = (po.pendingByMaterial || []).some(
    (row) => (Number(row.d0_15) || 0) + (Number(row.d16_30) || 0) + (Number(row.d31_60) || 0) + (Number(row.d60_plus) || 0) > 0
  );
  const pendingMaterialAgingRows = (po.pendingByMaterial || []).map((row) => ({
    label: row.material,
    d0_15: Number(row.d0_15) || 0,
    d16_30: Number(row.d16_30) || 0,
    d31_60: Number(row.d31_60) || 0,
    d60_plus: Number(row.d60_plus) || 0,
    detailsByKey: {
      d0_15: buildPendingPoTooltipDetails(row.itemsByKey?.d0_15 || []),
      d16_30: buildPendingPoTooltipDetails(row.itemsByKey?.d16_30 || []),
      d31_60: buildPendingPoTooltipDetails(row.itemsByKey?.d31_60 || []),
      d60_plus: buildPendingPoTooltipDetails(row.itemsByKey?.d60_plus || []),
    },
  }));
  const vendorRows = (po.vendorBreakdown || []).map((row) => ({
    label: row.name,
    po: Number(row.po) || 0,
    ap: Number(row.ap) || 0,
  }));
  const hasVendorData = vendorRows.some((row) => row.po + row.ap > 0);
  const materialPieData = (po.materialBreakdown || []).map((row, i) => ({
    id: i,
    value: row.value,
    label: row.name,
    color: COLORS[i % COLORS.length],
  }));
  const hasMaterialData = materialPieData.some((row) => Number(row.value) > 0);
  const soLedgerPieData = (po.soLedgerBreakdown || []).map((row, i) => ({
    id: i,
    value: row.value,
    label: row.name,
    color: COLORS[i % COLORS.length],
  }));
  const hasSoLedgerData = soLedgerPieData.some((row) => Number(row.value) > 0);
  const apNamePieData = (po.apNameBreakdown || []).map((row, i) => ({
    id: i,
    value: row.value,
    label: row.name,
    color: COLORS[(i + 3) % COLORS.length],
  }));
  const hasApNameData = apNamePieData.some((row) => Number(row.value) > 0);
  const carpetArea = Number(budget?.carpet_area) || 0;
  const psfRows = useMemo(() => {
    if (!carpetArea) return [];
    return [
      { label: "Budget / sq ft", value: budgetBase / carpetArea, color: "#3b82f6" },
      { label: "Spend / sq ft", value: budgetUsed / carpetArea, color: isOver ? "#dc2626" : "#10b981" },
    ];
  }, [carpetArea, budgetBase, budgetUsed, isOver]);
  const hasPsfData = psfRows.some((row) => Number(row.value) > 0);
  const poLevelRows = useMemo(
    () =>
      (po.distinctPOs || []).map((p) => ({
        po_number: p.po_number,
        po: Number(p.po_with_tax) || 0,
        canceled: Number(p.canceled) || 0,
        completed: Number(p.completed) || 0,
        pending: Number(p.pending) || 0,
      })),
    [po.distinctPOs]
  );
  const monthlyLabels = (po.monthlyTrend || []).map((row) => row.label);
  const hasMonthlyData = monthlyLabels.length > 0;
  const monthlySeries = useMemo(() => {
    const trend = po.monthlyTrend || [];
    const poGroups = trend.map((row) => groupTooltipItems(row.poItems || []));
    const grnGroups = trend.map((row) =>
      groupTooltipItems(row.grnItems || [], (item) =>
        [
          item.grn_date ? `GRN date: ${formatDate(item.grn_date)}` : null,
          item.grn_no ? `GRN ${item.grn_no}` : null,
          item.invoice_no ? `Invoice ${item.invoice_no}` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      )
    );
    const soGroups = trend.map((row) =>
      groupTooltipItems(row.soItems || [], (item) =>
        [item.vendor, item.material].filter(Boolean).join(" · ")
      )
    );
    const apGroups = trend.map((row) =>
      groupTooltipItems(row.apItems || [], (item) =>
        [
          item.vendor,
          item.material,
          item.invoice_no ? `Instrument ${item.invoice_no}` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      )
    );
    return [
      {
        key: "po",
        label: "PO amount",
        color: "#6366f1",
        values: trend.map((row) => row.po),
        metas: poGroups.map((g) => `${g.itemCount} item${g.itemCount === 1 ? "" : "s"} · ${g.poCount} PO${g.poCount === 1 ? "" : "s"}`),
        details: poGroups.map((g) => g.details),
      },
      {
        key: "grn",
        label: "GRN received",
        color: "#10b981",
        values: trend.map((row) => row.grn),
        metas: grnGroups.map((g) => `${g.itemCount} item${g.itemCount === 1 ? "" : "s"} · ${g.poCount} PO${g.poCount === 1 ? "" : "s"}`),
        details: grnGroups.map((g) => g.details),
      },
      {
        key: "so",
        label: "Service PO",
        color: "#0f766e",
        values: trend.map((row) => row.so || 0),
        metas: soGroups.map((g) => `${g.itemCount} order${g.itemCount === 1 ? "" : "s"}`),
        details: soGroups.map((g) => g.details),
      },
      {
        key: "ap",
        label: "AP voucher",
        color: "#c2410c",
        values: trend.map((row) => row.ap || 0),
        metas: apGroups.map((g) => `${g.itemCount} voucher${g.itemCount === 1 ? "" : "s"}`),
        details: apGroups.map((g) => g.details),
      },
    ].filter((series) => series.values.some((value) => Number(value) > 0));
  }, [po.monthlyTrend]);

  const utilizationPct = Math.min(Number(utilization) || 0, 100);

  return (
    <Box sx={{ ...cardShell, overflow: "hidden", boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        onClick={() => setOpen((p) => !p)}
        gap={1.5}
        sx={{
          px: 2.5,
          py: 1.75,
          cursor: "pointer",
          backgroundColor: open ? "#f8fbff" : "#fff",
          "&:hover": { backgroundColor: "#f8fbff" },
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5} minWidth={0}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, backgroundColor: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <StoreMallDirectory sx={{ color: "#3b82f6", fontSize: 22 }} />
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3} noWrap>
              {budget?.store_name || budget?.project_id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {budget?.project_id}
              {budget?.city || budget?.state ? ` · ${[budget?.city, budget?.state].filter(Boolean).join(", ")}` : ""}
              {budget?.channel ? ` · ${budget.channel}` : ""}
              {budget?.project ? ` · ${budget.project}` : ""}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
          <Chip label={isSubmitted ? "Closed" : "Open"} color={isSubmitted ? "success" : "warning"} size="small" variant="outlined" />
          <Chip label={`${po.poCount} POs`} size="small" variant="outlined" />
          {po.soCount > 0 && <Chip label={`${po.soCount} SOs`} size="small" variant="outlined" />}
          {po.apCount > 0 && <Chip label={`${po.apCount} APs`} size="small" variant="outlined" />}
          <Box sx={{ minWidth: 140 }}>
            <Stack direction="row" justifyContent="space-between" mb={0.25}>
              <Typography variant="caption" color="text.secondary">Utilized</Typography>
              <Typography variant="caption" fontWeight={700} color={isOver ? "error.main" : "primary.main"}>
                {utilization}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={utilizationPct}
              sx={{
                height: 6,
                borderRadius: 4,
                backgroundColor: "#e5e7eb",
                "& .MuiLinearProgress-bar": { backgroundColor: isOver ? "#dc2626" : "#3b82f6", borderRadius: 4 },
              }}
            />
          </Box>
          <Typography variant="body2" fontWeight={700}>{formatCurrency(totalBudget)}</Typography>
          <Tooltip title="Open store budget">
            <IconButton
              size="small"
              onClick={openStoreBudget}
              sx={{ color: "#1d4ed8", backgroundColor: "#eff6ff", "&:hover": { backgroundColor: "#dbeafe" } }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </Stack>
      </Stack>

      <Collapse in={open}>
        <Divider />
        <Box sx={{ p: 2.5, backgroundColor: "#fafcff" }}>
          <Stack direction="row" gap={1} flexWrap="wrap" mb={2}>
            {[
              { label: "Store Code", value: budget?.store_code },
              { label: "Work Type", value: budget?.work_type },
              { label: "Opening Date", value: budget?.store_opening_date ? new Date(budget.store_opening_date).toLocaleDateString("en-GB").replace(/\//g, "-") : "-" },
              { label: "Carpet Area", value: `${Number(budget?.carpet_area || 0).toLocaleString()} sq ft` },
              { label: "Std / Sq Ft", value: formatCurrency(budget?.std_per_sq_feet) },
            ].map((item) => (
              <Box key={item.label} sx={{ px: 1.5, py: 1, borderRadius: 1.5, backgroundColor: "#fff", border: "1px solid #e5e7eb", minWidth: 130 }}>
                <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{item.value || "-"}</Typography>
              </Box>
            ))}
          </Stack>

          <Stack direction="row" gap={1.5} flexWrap="wrap" mb={3}>
            <StatCard label="Total Budget" value={formatCurrency(totalBudget)} color="#1d4ed8" bg="#eff6ff" />
            <StatCard label="PO Amount" value={formatCurrency(po.totalPoWithTax)} sub={`Base: ${formatCurrency(po.totalPoBase)}`} sub1={`Tax: ${formatCurrency(po.totalPoTax)}`} color="#4f46e5" bg="#eef2ff" />
            <StatCard label="Service PO" value={formatCurrency(soAmount)} sub={`${po.soCount} order${po.soCount === 1 ? "" : "s"}`} color="#0f766e" bg="#f0fdfa" />
            <StatCard label="GRN Amount (w tax)" value={formatCurrency(po.totalGrnWithTax)} sub={`Base: ${formatCurrency(po.totalGrnWithoutTax)}`} sub1={`Tax: ${formatCurrency(po.totalGrnWithTax - po.totalGrnWithoutTax)}`} color="#0f766e" bg="#f0fdf4" />
            <StatCard label="Canceled Amount" value={formatCurrency(po.totalCanceled)} sub={`Base: ${formatCurrency(po.canceledItemBAmount)}`} sub1={`Tax: ${formatCurrency(po.totalCanceled - po.canceledItemBAmount)}`} color="#6b7280" bg="#f9fafb" />
            <StatCard label="Pending For GRN" value={formatCurrency(po.totalPending)} sub={`Base: ${formatCurrency(po.pendingItemValue)}`} sub1={`Tax: ${formatCurrency(po.totalPending - po.pendingItemValue)}`} color="#d97706" bg="#fffbeb" />
            <StatCard label="AP Voucher" value={formatCurrency(apAmount)} sub={`${po.apCount} voucher${po.apCount === 1 ? "" : "s"}`} color="#c2410c" bg="#fff7ed" />
            <StatCard label="Net PO" value={formatCurrency(po.totalPoWithTax - po.totalCanceled)} sub={`Base: ${formatCurrency(po.totalPoBase - po.canceledItemBAmount)}`} sub1={`Tax: ${formatCurrency(po.totalPoTax - (po.totalCanceled - po.canceledItemBAmount))}`} color="#0f5c0f" bg="#e5f5e3" />
            <StatCard label={isOver ? "Budget Exceeded" : "Budget Remaining"} value={formatCurrency(isOver ? exceeded : remaining)} color={isOver ? "#dc2626" : "#16a34a"} bg={isOver ? "#fef2f2" : "#f0fdf4"} />
          </Stack>

          {(open && (hasPoStatusData || hasSectionData || hasBudgetVsActualData)) && (
            <Stack direction={{ xs: "column", lg: "row" }} gap={2} mb={2} alignItems="stretch">
              {hasPoStatusData && (
                <ChartCard title="PO Status Split" subtitle="Base amount excluding tax" sx={{ flex: 1, minWidth: 260 }}>
                  <D3DonutChart data={poStatusPieData} height={240} centerLabel="Total" />
                </ChartCard>
              )}
              {hasSectionData && (
                <ChartCard title="Section Breakdown" subtitle="GRN value by section" sx={{ flex: 1, minWidth: 260 }}>
                  <D3DonutChart data={sectionPieData} height={240} centerLabel="GRN" />
                </ChartCard>
              )}
              {hasBudgetVsActualData && (
                <ChartCard
                  title="Budget vs Actuals"
                  subtitle="Budget vs GRN, pending, cancelled and service PO"
                  sx={{ flex: hasPoStatusData || hasSectionData ? 2 : 1, minWidth: 300 }}
                >
                  <D3BudgetVsActual
                    budget={budgetBase}
                    grn={grnBase}
                    pending={pendingBase}
                    cancelled={cancelledBase}
                    so={soAmount}
                    height={240}
                  />
                </ChartCard>
              )}
            </Stack>
          )}

          {(open && (hasVendorData || hasPendingByMaterialData)) && (
            <Stack direction={{ xs: "column", lg: "row" }} gap={2} mb={2} alignItems="stretch">
              {hasVendorData && (
                <ChartCard title="Vendor concentration" subtitle="PO present vs AP voucher paid. Click the PO bar or vendor name for PO Excel, or the AP bar to download that vendor’s AP voucher file." sx={{ flex: 1, minWidth: 280 }}>
                  <D3GroupedHorizontalBarChart
                    rows={vendorRows}
                    series={VENDOR_CONCENTRATION_SERIES}
                    height={280}
                    onBarClick={(row) => {
                      if (row.key === "ap") downloadVendorApExcel(row.label, getApDetails(budget));
                      else downloadVendorExcel(row.label, budget?.po_details || []);
                    }}
                  />
                </ChartCard>
              )}
              {hasPendingByMaterialData && (
                <ChartCard
                  title="Pending by Material × Aging"
                  subtitle="Pending amount by material, stacked by days since PO date"
                  sx={{ flex: 1, minWidth: 300 }}
                >
                  <D3StackedHorizontalBarChart
                    rows={pendingMaterialAgingRows}
                    series={PENDING_AGING_SERIES}
                    height={280}
                  />
                </ChartCard>
              )}
            </Stack>
          )}

          {(open && (hasMaterialData || hasPsfData || hasSoLedgerData || hasApNameData)) && (
            <Stack direction={{ xs: "column", lg: "row" }} gap={2} mb={2} alignItems="stretch">
              {hasMaterialData && (
                <ChartCard title="Material mix" subtitle="PO amount by material" sx={{ flex: 1, minWidth: 260 }}>
                  <D3DonutChart data={materialPieData} height={240} centerLabel="PO" />
                </ChartCard>
              )}
              {hasSoLedgerData && (
                <ChartCard title="Service PO by ledger" subtitle="Service amount by service ledger" sx={{ flex: 1, minWidth: 260 }}>
                  <D3DonutChart data={soLedgerPieData} height={240} centerLabel="SO" />
                </ChartCard>
              )}
              {hasApNameData && (
                <ChartCard title="AP voucher by name" subtitle="Voucher amount by payee" sx={{ flex: 1, minWidth: 260 }}>
                  <D3DonutChart data={apNamePieData} height={240} centerLabel="AP" />
                </ChartCard>
              )}
              {hasPsfData && (
                <ChartCard title="Spend vs budget per sq ft" subtitle="Makes stores of different sizes comparable" sx={{ flex: 1, minWidth: 260 }}>
                  <D3SimpleBarChart rows={psfRows} height={240} />
                </ChartCard>
              )}
            </Stack>
          )}

          {open && hasPoLevelData && (
            <Box mb={2}>
              <ChartCard title="PO Level — Amount vs GRN" subtitle="PO amount (with tax) vs GRN received" sx={{ width: "100%" }}>
                <D3GroupedBarChart rows={poLevelRows} height={300} />
              </ChartCard>
            </Box>
          )}

          {open && hasMonthlyData && (
            <Box mt={2}>
              <ChartCard title="Monthly PO vs GRN" subtitle="PO by PO date, GRN by GRN date, service PO by order date, AP voucher by voucher date">
                <D3MultiLineChart
                  labels={monthlyLabels}
                  series={monthlySeries}
                  height={260}
                />
              </ChartCard>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const EXAMPLE_DATA = [
  {
    id: "budget-1",
    channel: "EBO",
    work_type: "RENOVATION",
    store_name: "THOR-SS-KOLKATA",
    store_code: "THOR023",
    project: "THOR",
    city: "KOLKATA",
    state: "WEST BENGAL",
    store_opening_date: "2026-04-07",
    carpet_area: "0654",
    std_per_sq_feet: "06783",
    total_budget: 4436082,
    is_submitted: false,
    email: "amitp@thehouseofrare.com",
    po_details: [
      { id: "RR/PO/07790/25-26-WH/GRN/13010/25-26-WRMS/25-26/1091", material: "FIXTURE", vendor_name: "Wave Rider Marketing Solutions", po_number: "RR/PO/07790/25-26", po_remark: null, po_date: "2025-09-30", po_value: 114000, po_tax: 20520, invoice_no: "WRMS/25-26/1091", invoice_date: "2025-10-11", invoice_value_without_tax: 114000, invoice_value_with_tax: 134520, grn_date: "2025-11-06", grn_no: "WH/GRN/13010/25-26", store_name: "THOR-SS-KOLKATA", site_code: "THOR023", item_status: "COMPLETED", barcode: "RR257556" },
      { id: "RR/PO/08286/25-26-WH/GRN/12901/25-26-WRMS/25-26/1129", material: "FIXTURE", vendor_name: "Wave Rider Marketing Solutions", po_number: "RR/PO/08286/25-26", po_remark: null, po_date: "2025-10-16", po_value: 337500, po_tax: 60750, invoice_no: "WRMS/25-26/1129", invoice_date: "2025-10-18", invoice_value_without_tax: 337500, invoice_value_with_tax: 398250, grn_date: "2025-11-05", grn_no: "WH/GRN/12901/25-26", store_name: "THOR-SS-KOLKATA", site_code: "THOR023", item_status: "COMPLETED", barcode: "RR267797" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 10880, invoice_value_with_tax: 12838.4, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245063" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-1", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 512, invoice_value_with_tax: 604.16, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245064" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-2", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 54800, invoice_value_with_tax: 64664, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR241854" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-3", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 63000, invoice_value_with_tax: 78624, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR241852" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-4", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 8500, invoice_value_with_tax: 10030, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245066" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-5", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 37100, invoice_value_with_tax: 45164, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR241859" },
      { id: "RR/PO/05807/25-26-WH/GRN/12679/25-26-TAS/1802/25-26-6", material: "SURVILLANCE", vendor_name: null, po_number: "RR/PO/05807/25-26", po_remark: null, po_date: "2025-08-13", po_value: 206832, po_tax: 37229.76, invoice_no: "TAS/1802/25-26", invoice_date: "2025-10-17", invoice_value_without_tax: 540, invoice_value_with_tax: 637.2, grn_date: "2025-10-30", grn_no: "WH/GRN/12679/25-26", store_name: "RTPL(KOL)", site_code: "ABHISHEK", item_status: "COMPLETED", barcode: "RR245062" },
    ],
  },
];

const initial_state = () => {
    const obj = {};

    storeBudgetTrackerReport?.forEach((attr) => {
      obj[attr.key] = getColDefaultValue(attr);
    });
    return obj;
  };

export default function StoreBudgetReport() {
  const [budgetList, setBudgetList] = useState([]);
  const [storeFilter, setStoreFilter] = useState(initial_state());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [storeData, setStoreData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [hasFetched, setHasFetched] = useState(false);
  const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

  const getStoreBudget = async () => {
      try {
        setLoadingOpen(true);
        if (["SELECT", ""].includes(storeFilter?.store_name)) {
          throw new Error("Please select a valid store name");
        }
        const data = {
            "store_name": storeFilter?.store_name || "",
            "start_date": storeFilter?.start_date || "",
            "end_date": storeFilter?.end_date || "",
          }
        await pythonVmInstance({
          url: `/store-budget-tracker/report/get-store-budget`,
          params: data,
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        }).then((res) => {
          setBudgetList(res?.data || []);
          setHasFetched(true);
          setLoadingOpen(false);
          setSnackBarOpen(true);
          setSnackBar({
            ...snackBar,
            message: "successfully fetched the report",
            severity: "success",
            anchorOrigin: { horizontal: "center", vertical: "top" },
          });
        });
      } catch (error) {
        setSnackBarOpen(true);
        setLoadingOpen(false);
        setSnackBar({
          ...snackBar,
          message: error.message ? error.message : "Failed to fetch report",
          severity: "error",
          anchorOrigin: { horizontal: "center", vertical: "top" },
        });
      }
    };
    const getCreatedStoreBudgetStores = async () => {
        try {
          setLoadingOpen(true);
          await pythonVmInstance({
            url: `/store-budget-tracker/report/get-budgeted-store`,
            headers: {
              "Content-Type": "application/json",
            },
            method: "GET",
          }).then((res) => {
            setStoreData(res?.data);
            setLoadingOpen(false);
          });
        } catch (error) {
          setSnackBarOpen(true);
          setLoadingOpen(false);
          setSnackBar({
            ...snackBar,
            message: "Failed to download file",
            severity: "error",
            anchorOrigin: { horizontal: "center", vertical: "top" },
          });
        }
      };

  useEffect(() => {getCreatedStoreBudgetStores() }, []);

  const filtered = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return budgetList.filter((b) => {
      const matchStore = !storeFilter || b?.store_name?.toLowerCase().includes(storeFilter?.store_name?.toLowerCase());
      const createdAt = b?.store_opening_date ? new Date(b.store_opening_date) : null;
      const matchFrom = !fromDate || (createdAt && createdAt >= new Date(fromDate));
      const matchTo = !toDate || (createdAt && createdAt <= new Date(toDate + "T23:59:59"));
      const matchSearch =
        !query ||
        [b?.project_id, b?.store_name, b?.store_code, b?.channel, b?.project, b?.city, b?.state, b?.work_type]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      return matchStore && matchFrom && matchTo && matchSearch;
    });
  }, [budgetList, storeFilter, fromDate, toDate, searchText]);

  const summary = useMemo(() => {
    const totalBudget = filtered.reduce((s, b) => s + (Number(b?.total_budget) || 0), 0);
    const totals = filtered.reduce(
      (acc, b) => {
        const po = computePoSummary(b?.po_details || [], getSoDetails(b), getApDetails(b));
        acc.totalPoAmount += po.overallPoAmount;
        acc.totalSoAmount += Number(po.soAmount) || 0;
        acc.totalApAmount += Number(po.apAmount) || 0;
        return acc;
      },
      { totalPoAmount: 0, totalSoAmount: 0, totalApAmount: 0 }
    );
    return {
      total: filtered.length,
      open: filtered.filter((b) => !b?.is_submitted && b?.status !== "submitted").length,
      closed: filtered.filter((b) => b?.is_submitted || b?.status === "submitted").length,
      totalBudget,
      totalPoAmount: totals.totalPoAmount,
      totalSoAmount: totals.totalSoAmount,
      totalApAmount: totals.totalApAmount,
    };
  }, [filtered]);

  const utilizationByProject = useMemo(
    () =>
      filtered.map((b) => {
        const po = computePoSummary(b?.po_details || [], getSoDetails(b), getApDetails(b));
        const total = Number(b?.total_budget) || 0;
        const used = (Number(po.overallPoAmount) || 0) + (Number(po.soAmount) || 0);
        const pct = total ? (used / total) * 100 : 0;
        return {
          label: b.project_id || b.store_name || "Budget",
          value: Number(pct.toFixed(1)),
          color: pct > 100 ? "#dc2626" : pct > 80 ? "#f59e0b" : "#3b82f6",
          meta: `${formatCurrency(used)} of ${formatCurrency(total)}`,
        };
      }),
    [filtered]
  );

  const selectedStore = storeData
    ?.map((st) => ({
      name: st.store_name,
      key: st.store_name,
    }));

  return (
    <Box padding={2}>
      <Stack padding={2} boxShadow="0px 1px 3px rgba(0, 0, 0, 0.2)" borderRadius={2} spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5}>
          <Box>
            <Typography variant="h6">Store Budget Report</Typography>
            <Typography variant="body2" color="text.secondary">
              Select a store and date range, then fetch utilization, PO, service PO, AP voucher, and GRN details.
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1.5, backgroundColor: "#f8fbff" }}>
          <Stack direction="row" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
            {storeBudgetTrackerReport?.map((row) => {
              let option_data = row.option_data ? row.option_data : {};
              if (row?.key === "store_name") {
                option_data = {
                  type: arrType,
                  list: selectedStore,
                };
              }
              return (
                <RenderInputField
                  key={row.key}
                  column={{ ...row, option_data }}
                  data={storeFilter}
                  setData={setStoreFilter}
                  width="24%"
                  selectOptObjName={"option_data"}
                />
              );
            })}
            <Stack spacing={0.5} minHeight={72} sx={{ width: "auto", minWidth: 0 }}>
              <InputLabel sx={{ visibility: "hidden", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.02em", lineHeight: 1.5 }}>
                Fetch
              </InputLabel>
              <Button variant="contained" onClick={getStoreBudget} sx={{ height: 40, minHeight: 40, px: 2.5 }}>
                Fetch Report
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <StatCard label="Total Budgets" value={summary.total} color="#1d4ed8" bg="#eff6ff" />
          <StatCard label="Open" value={summary.open} color="#d97706" bg="#fffbeb" />
          <StatCard label="Closed" value={summary.closed} color="#16a34a" bg="#f0fdf4" />
          <StatCard label="Total Budget Value" value={formatCurrency(summary.totalBudget)} color="#7c3aed" bg="#f5f3ff" />
          <StatCard label="Total PO Amount" value={formatCurrency(summary.totalPoAmount)} color="#0f766e" bg="#f0fdf4" />
          <StatCard label="Total Service PO" value={formatCurrency(summary.totalSoAmount)} color="#0f766e" bg="#f0fdfa" />
          <StatCard label="Total AP Voucher" value={formatCurrency(summary.totalApAmount)} color="#c2410c" bg="#fff7ed" />
        </Stack>

        {hasFetched && utilizationByProject.length > 0 && (
          <ChartCard
            title="Utilization by project"
            subtitle="Net PO + service PO as a share of total budget. Red is over 100%."
            sx={{ width: { xs: "100%", md: "50%" }, maxWidth: { md: "50%" }, boxSizing: "border-box" }}
          >
            <D3HorizontalBarChart
              rows={utilizationByProject}
              height={Math.max(160, utilizationByProject.length * 42)}
              valueFormatter={formatPct}
              domainMax={100}
            />
          </ChartCard>
        )}

        {hasFetched && (
          <TextField
            size="small"
            placeholder="Search project, store, city, channel..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} fontSize="small" />,
            }}
            sx={{ maxWidth: 360 }}
          />
        )}

        <Stack gap={1.5}>
          {filtered.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: "1px dashed #d1d5db",
                borderRadius: 1.5,
                backgroundColor: "#fafcff",
              }}
            >
              <StoreMallDirectory sx={{ fontSize: 36, color: "#93c5fd", mb: 1 }} />
              <Typography color="text.secondary">
                {hasFetched
                  ? "No store budgets found for the selected filters."
                  : "Select a store and date range, then click Fetch Report."}
              </Typography>
            </Box>
          ) : (
            filtered.map((budget) => <BudgetCard key={budget.id || budget.project_id} budget={budget} />)
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
